/**
 * The Defender board's visual grammar, in one place so the setup panel, the KPI
 * strip, the breakdown charts and the trend lines can never disagree.
 *
 * Three things live here that the rest of the feature must not duplicate:
 *
 * 1. **`access_state` copy, keyed off the generated union.** The backend
 *    distinguishes six ways a panel can be in — `ok` plus five distinct ways to
 *    have no data — because a 403, a 404 and a 400 are three different tickets
 *    for three different people. `ACCESS_COPY` is typed
 *    `Record<DefenderAccessState, …>`, so the *compiler* keeps it exhaustive: a
 *    seventh state added in `nova/defender/models.py` fails the build here
 *    rather than rendering as a blank card.
 * 2. **What an empty `missing_permissions` means, per state.** It is populated
 *    only by a real 403. Empty therefore means "nothing has been *proved*
 *    missing", which in the `no_credential` state means nothing was ever asked —
 *    not that nothing is missing. `namesMissingPermissions` says whether the
 *    list is evidence in this state, and `evidenceNote` says what it is not.
 *    Rendering "0 permissions missing" as reassurance is the §5 defect this
 *    field exists to make impossible.
 * 3. **A hard rule against zero.** Every formatter here renders `null` as an em
 *    dash, and no tone helper ever returns `success` for `null`. Every failable
 *    counter on this board is `number | null` where `0` is a measured zero and
 *    `null` is a read that never answered: zero open incidents is good news,
 *    unknown is not, and they must never render identically.
 */

import {
  Ban,
  Bug,
  CircleAlert,
  CircleCheck,
  CircleEllipsis,
  CloudOff,
  type IconComponent,
  Info,
  KeyRound,
  OctagonAlert,
  PackageX,
  ShieldOff,
  TriangleAlert,
} from "@norican/icons";

import type {
  DefenderBreakdown,
  DefenderBucket,
  DefenderCohortSuppression,
  DefenderIdentityHealth,
  DefenderIncidents,
} from "@/lib/api";
import {
  formatCount as formatCountShared,
  formatPercent as formatPercentShared,
  heatLegend,
  type StatusTone,
} from "@/lib/charts/config";

/** The one "nothing to show" glyph on this board, exported so tiles reuse it. */
export const EM_DASH = "—";

/* ------------------------------------------------------------------------ *
 * Access state — this board's primary state, not an afterthought
 * ------------------------------------------------------------------------ */

/**
 * The panel access states, projected out of the generated payload.
 *
 * Never re-typed by hand: this is what makes `ACCESS_COPY` exhaustive against
 * the backend rather than against someone's memory of it.
 */
export type DefenderAccessState = DefenderIncidents["access_state"];

/** MDI's rolled-up verdict. `unknown` is a real answer, and it is not `healthy`. */
export type DefenderHealthState = DefenderIdentityHealth["state"];

export interface AccessStateCopy {
  /** Panel heading — names the *kind* of problem, not the symptom. */
  title: string;
  /** Card accent / `Alert` tone. */
  tone: StatusTone;
  /** Icon paired with the tone — state is never colour-alone (§3.2). */
  icon: IconComponent;
  /** Whose problem this is, in one clause. */
  owner: string;
  /** What to do about it, most direct action first. */
  remedy: readonly string[];
  /** Short label for a `Badge` when the state is mentioned inline. */
  badge: string;
  /**
   * Whether `missing_permissions` is evidence in this state.
   *
   * True only where Graph answered 403 — the one answer that proves a permission
   * is not granted. Everywhere else the list is empty because nothing was
   * proved, and an empty list must not be rendered as "nothing is missing".
   */
  namesMissingPermissions: boolean;
  /** What an empty `missing_permissions` means here, in words. */
  evidenceNote: string;
}

/**
 * What each `access_state` means, whose problem it is, and what to do.
 *
 * The three states that are easiest to blur, and must not be:
 *
 * - **`consent_missing`** (403) — a *human* must grant an application
 *   permission. This is the expected state of every panel on this board today.
 *   Actionable by a Global Administrator, in two places, and then it may take
 *   ~24 h to reach the running app's token.
 * - **`not_provisioned`** (404) — the Defender product behind the panel is not
 *   deployed or not onboarded in the tenant. Granting a permission would change
 *   nothing: there is nothing there to read. A fact about the estate, a
 *   different ticket, and never "no data".
 * - **`request_rejected`** (400) — NOVA asked wrongly. The credential is fine
 *   and the product is there; NOVA's own `$filter`/`$expand`/`$top` is wrong or
 *   the collection's contract changed upstream. Nobody in IT can configure this
 *   away: it reads as "raise a ticket against NOVA", not as a customer problem.
 */
export const ACCESS_COPY: Record<DefenderAccessState, AccessStateCopy> = {
  ok: {
    title: "Reading live from Microsoft Graph",
    tone: "success",
    icon: CircleCheck,
    owner: "Nothing to do.",
    remedy: [],
    badge: "Live",
    namesMissingPermissions: true,
    evidenceNote:
      "Every read this panel needs answered. Any permission listed as refused below belongs to one " +
      "endpoint that failed while the rest of the panel kept rendering.",
  },
  no_credential: {
    title: "NOVA has no Microsoft Graph credential",
    tone: "warning",
    icon: KeyRound,
    owner:
      "A NOVA deployment gap. Nothing was asked of Graph at all, so nothing is known about consent either way.",
    remedy: [
      "In Azure — assign the managed identity nova-app-dev to the nova-api Container App, then grant it the four app roles below.",
      "Anywhere else — set AZURE_TENANT_ID, AZURE_CLIENT_ID and AZURE_CLIENT_SECRET to the app's own service principal.",
      "An `az login` on the host is deliberately not used: NOVA never authenticates as a person, so it can never borrow a signed-in person's wider rights.",
    ],
    badge: "No credential",
    namesMissingPermissions: false,
    evidenceNote:
      "No permission is listed as missing here, and that is not reassurance: with no credential " +
      "nothing was requested, so nothing has been shown to be missing — or to be granted. The four " +
      "permissions below are what this board will ask for once it can ask at all.",
  },
  consent_missing: {
    title: "Microsoft Graph refused the read — consent has not been granted",
    tone: "warning",
    icon: ShieldOff,
    owner:
      "A human has to grant an application permission. NOVA authenticated fine and asked correctly; the token does not carry the role.",
    remedy: [
      "Grant the named permission on the app registration NO-AR-NOVA and admin-consent it.",
      "Then assign the same app role to the managed identity nova-app-dev — both places, or the running board still cannot read.",
      "Allow up to ~24 h for the role to reach a managed-identity token. The board lights up on its own once it does: no redeploy, no code change.",
      "Graph also answers 403 when a product *licence* is missing, and the error code that tells the two apart could not be verified — so the raw Graph code is shown verbatim rather than guessed at.",
    ],
    badge: "403 consent missing",
    namesMissingPermissions: true,
    evidenceNote:
      "Graph answered 403, so the permission names below are measured, not inferred: those are the " +
      "exact application permissions to grant.",
  },
  not_provisioned: {
    title: "The Defender product behind this panel is not deployed here",
    tone: "warning",
    icon: PackageX,
    owner:
      "A fact about the estate, not a permission problem. Graph answered 404: there is nothing at this collection to read.",
    remedy: [
      "Granting a permission would not help — a role cannot be assigned over a product that is not onboarded.",
      "Defender for Identity with no deployed sensors, or a tenant not onboarded to the Defender portal, both answer this way.",
      "The fix is a deployment or licensing decision (MDI standalone, or E5/A5/G5/F5 Security), and it is a different ticket from consent.",
    ],
    badge: "404 not provisioned",
    namesMissingPermissions: false,
    evidenceNote:
      "No permission is listed as missing, and none has been ruled out either: a 404 says nothing " +
      "about consent. Whether this permission is granted is still unknown.",
  },
  request_rejected: {
    title: "Microsoft Graph rejected NOVA's request",
    tone: "danger",
    icon: Bug,
    owner:
      "A NOVA-side bug. The credential is fine and the product is there — the query NOVA sent is wrong.",
    remedy: [
      "Raise a ticket against NOVA. There is nothing for an administrator to configure, grant or license here.",
      "Usually upstream contract drift: a $filter, $expand or $top NOVA sends is no longer accepted on this collection.",
      "Nothing about the numbers this panel would have shown can be inferred from this state — they were never computed.",
    ],
    badge: "400 rejected by Graph",
    namesMissingPermissions: false,
    evidenceNote:
      "No permission is listed as missing: a 400 is NOVA's mistake, not a refusal, and it proves " +
      "nothing either way about consent.",
  },
  unavailable: {
    title: "Microsoft Graph did not answer",
    tone: "warning",
    icon: CloudOff,
    owner:
      "Upstream, or an identity fault. Either Graph returned 5xx / did not complete, or it refused NOVA's token twice — once more after a fresh one was minted.",
    remedy: [
      "Transient by nature: the next poll may well succeed and the board recovers on its own.",
      "A token refused twice is an identity fault, not a missing consent, which is why this deliberately does not read as 403.",
      "If it persists, check the Microsoft 365 service health for Microsoft Graph before touching NOVA.",
    ],
    badge: "Unavailable",
    namesMissingPermissions: false,
    evidenceNote:
      "No permission is listed as missing: a 5xx or a refused token says nothing about whether " +
      "consent has been granted.",
  },
};

/**
 * The sentence every panel with no data carries.
 *
 * The point of this board's state handling: a panel with no data shows
 * *nothing*, and says why, rather than showing `0`. A 404 rendered as zero open
 * incidents would read as a quiet, well-defended estate — the flattering
 * direction §5 and §8 exist to forbid.
 */
export const NOT_ZERO_NOTE =
  "No measurement was taken, so this panel is blank rather than zero. Nothing here is simulated, " +
  "estimated, or carried over from an earlier read.";

/**
 * What `truncated: true` means, said next to the numbers rather than hidden.
 *
 * Paging stopped at NOVA's page cap, so every count in the panel is a floor. §8:
 * a count is a lower bound until proven otherwise, and a floor presented as a
 * census is wrong in the flattering direction.
 */
export const TRUNCATED_NOTE =
  "Microsoft Graph returned more than NOVA reads in one pass, so paging stopped at its page cap. " +
  "Every count in this panel is a lower bound, not a census — the real figures are higher by an " +
  "unknown amount, and a breakdown's shares are shares of what was read.";

/**
 * The state contract every `/defender/*` payload shares — the backend's
 * `DefenderPanel` base, projected out of the generated type rather than re-typed
 * by hand. All five payloads satisfy it structurally, so a panel component can
 * take any of them without knowing which route it came from.
 */
export type DefenderPanelState = Pick<
  DefenderIncidents,
  | "configured"
  | "access_state"
  | "status_detail"
  | "collected_at"
  | "window"
  | "cache_ttl_sec"
  | "required_permissions"
  | "missing_permissions"
  | "truncated"
  | "errors"
>;

/** Whether a payload carries measured numbers, as opposed to a reason it does not. */
export function isPanelOk(panel: { access_state: DefenderAccessState }): boolean {
  return panel.access_state === "ok";
}

/* ------------------------------------------------------------------------ *
 * Tone helpers — `null` is never `success`
 * ------------------------------------------------------------------------ */

/**
 * Tone for a counter where anything above zero is bad (open incidents,
 * unhealthy sensors, open health issues).
 *
 * `null` is `neutral`: a read that never answered is not good news. `0` is
 * `success` because it is a *measured* zero — that distinction is the whole
 * reason these fields are nullable.
 */
export function problemTone(value: number | null | undefined): StatusTone {
  if (value == null) {
    return "neutral";
  }
  return value > 0 ? "danger" : "success";
}

/**
 * Age at which an unresolved incident stops being "being worked" and becomes a
 * finding. Shared so the KPI tile and the incident section cannot disagree.
 */
export const STALE_INCIDENT_DAYS = 7;

/**
 * Tone for an oldest-open figure — the place on this board where `null` means
 * two opposite things.
 *
 * Shared by the oldest unresolved incident and the oldest open MDI health issue,
 * on the same 7-day edge: one threshold for the board, so "how long has this been
 * sitting there" cannot mean two different things on two panels.
 *
 * The age is null either because nothing is open (the good case) or because the
 * read failed, so this reads the open *count* as well: nothing open earns
 * `success`, a failed read stays `neutral`, and neither is ever inferred from the
 * age alone.
 */
export function oldestOpenTone(open: number | null, oldestDays: number | null): StatusTone {
  if (open === 0) {
    return "success";
  }
  if (open == null || oldestDays == null) {
    return "neutral";
  }
  return oldestDays >= STALE_INCIDENT_DAYS ? "warning" : "success";
}

/** As `problemTone`, but a non-zero count is a caution rather than a failure. */
export function cautionTone(value: number | null | undefined): StatusTone {
  if (value == null) {
    return "neutral";
  }
  return value > 0 ? "warning" : "success";
}

/**
 * Tone for a figure that is volume, not a verdict (alerts created, incidents
 * created). High alert volume is not "bad" — it is the base every mix is a share
 * of — so it wears the brand accent, and `null` stays neutral.
 */
export function volumeTone(value: number | null | undefined): StatusTone | "accent" {
  return value == null ? "neutral" : "accent";
}

/** The caption a nulled counter carries, so "unknown" is never mistaken for "none". */
export function unknownNote(what: string): string {
  return `${what} could not be read — unknown, not zero.`;
}

/* ------------------------------------------------------------------------ *
 * Health verdicts — a status scale
 * ------------------------------------------------------------------------ */

export const HEALTH_LABEL: Record<DefenderHealthState, string> = {
  healthy: "Healthy",
  degraded: "Degraded",
  failing: "Failing",
  unknown: "Unknown",
};

export const HEALTH_TONE: Record<DefenderHealthState, StatusTone> = {
  healthy: "success",
  degraded: "warning",
  failing: "danger",
  unknown: "neutral",
};

export const HEALTH_ICON: Record<DefenderHealthState, IconComponent> = {
  healthy: CircleCheck,
  degraded: TriangleAlert,
  failing: OctagonAlert,
  unknown: CircleEllipsis,
};

/** What each verdict actually asserts — `unknown` most of all. */
export const HEALTH_DETAIL: Record<DefenderHealthState, string> = {
  healthy: "Every sensor MDI reported is healthy and no health issue is open.",
  degraded: "Some sensors are not healthy, or health issues are open below critical severity.",
  failing: "A high-severity health issue is open, or no sensor is reporting healthy.",
  unknown:
    "Nothing could be read, so there is no verdict. This is not a healthy board — it is an unmeasured one.",
};

/* ------------------------------------------------------------------------ *
 * Severity — a STATUS scale, never a categorical one (§3.2)
 * ------------------------------------------------------------------------ */

const dangerScale = heatLegend("danger");
const neutralScale = heatLegend("neutral");

/** The muted token every "no attribution" mark wears — `unknown` and `Other`. */
export const MUTED_FILL = "var(--norican-bg-muted)";

export interface BucketStatus {
  /** Display label — the upstream string, tidied for a legend, never re-mapped. */
  label: string;
  tone: StatusTone;
  /** Chart fill. A DS token or a step of a DS sequential scale — never hex (§3.1). */
  color: string;
  /** Icon paired with the colour, so severity is never colour-alone (§3.2). */
  icon: IconComponent;
}

/**
 * Graph's Defender severities, most severe first.
 *
 * An **ordering hint, never a validator** (§8, and the same tuple as
 * `nova.defender.service.KNOWN_SEVERITIES`): a severity Microsoft ships tomorrow
 * is still counted, still charted and still labelled — it just falls through to
 * the unrecognised fallback below instead of getting an assigned rung on the
 * scale. Nothing here raises on an unknown value.
 */
export const SEVERITY_ORDER: readonly string[] = [
  "high",
  "medium",
  "low",
  "informational",
  "unknown",
];

const SEVERITY_STATUS: Record<string, BucketStatus> = {
  high: {
    label: "High",
    tone: "danger",
    color: dangerScale[4] ?? "var(--norican-danger)",
    icon: OctagonAlert,
  },
  medium: {
    label: "Medium",
    tone: "warning",
    color: "var(--norican-warning)",
    icon: TriangleAlert,
  },
  low: { label: "Low", tone: "neutral", color: neutralScale[2] ?? MUTED_FILL, icon: CircleAlert },
  informational: {
    label: "Informational",
    tone: "neutral",
    color: neutralScale[1] ?? MUTED_FILL,
    icon: Info,
  },
  unknown: { label: "Unknown", tone: "neutral", color: MUTED_FILL, icon: CircleEllipsis },
};

/**
 * Severity semantics for a raw Graph string.
 *
 * Unrecognised values get the danger token and an alert glyph rather than the
 * muted "unknown" treatment: a severity NOVA has never seen is a real severity
 * on real detections, and hiding it in grey would read as noise. `unknown` — the
 * explicit fold for an absent or unusable value — is the one that goes muted,
 * because there is genuinely nothing to attribute.
 */
export function severityStatus(label: string): BucketStatus {
  const known = SEVERITY_STATUS[label.trim().toLowerCase()];
  if (known) {
    return known;
  }
  return {
    label,
    tone: "danger",
    color: dangerScale[3] ?? "var(--norican-danger)",
    icon: ShieldOff,
  };
}

/**
 * Sensor-health semantics. `healthy` is the only good answer: the backend counts
 * every other status — `unknown` included — as unhealthy, because a sensor whose
 * state cannot be read is not a healthy sensor.
 */
export function sensorHealthStatus(label: string): BucketStatus {
  const normalised = label.trim().toLowerCase();
  if (normalised === "healthy") {
    return {
      label: "Healthy",
      tone: "success",
      color: "var(--norican-success)",
      icon: CircleCheck,
    };
  }
  if (normalised === "unknown") {
    return {
      label: "Unknown",
      tone: "neutral",
      color: MUTED_FILL,
      icon: CircleEllipsis,
    };
  }
  return {
    label,
    tone: "danger",
    color: dangerScale[3] ?? "var(--norican-danger)",
    icon: OctagonAlert,
  };
}

/**
 * Fill for an ordinal age band — one hue, light to dark with age (§3.4,
 * sequential = magnitude).
 *
 * The `unknown` band takes the muted token instead of a rung on the ramp: an
 * incident with no usable creation timestamp has no age, and placing it on an
 * age ramp would invent one.
 */
export function ageBandFill(index: number, total: number, isUnknown: boolean): string {
  if (isUnknown) {
    return MUTED_FILL;
  }
  const step = total <= 1 ? dangerScale.length - 1 : Math.round((index / (total - 1)) * 4);
  return (
    dangerScale[Math.min(Math.max(step, 0), dangerScale.length - 1)] ?? "var(--norican-danger)"
  );
}

/* ------------------------------------------------------------------------ *
 * Breakdowns — buckets, folds and what a bucket flag means
 * ------------------------------------------------------------------------ */

/** Total across a breakdown's buckets — the figure a donut's hole carries. */
export function bucketTotal(buckets: readonly DefenderBucket[]): number {
  return buckets.reduce((sum, bucket) => sum + bucket.count, 0);
}

/** A bucket's share of its breakdown, or `null` when the breakdown is empty. */
export function bucketShare(count: number, total: number): number | null {
  return total > 0 ? (count / total) * 100 : null;
}

/**
 * The badge a bucket carries when it is not a plain labelled value.
 *
 * Both flags are honesty affordances, not decoration: `is_other` is the
 * unlabelled row that absorbed sub-floor cohorts, and `recognised: false` is
 * either the explicit `unknown` fold or a value NOVA has never seen. A reader
 * must be able to tell those from an ordinary category at a glance.
 */
export function bucketFlag(bucket: DefenderBucket): { label: string; hint: string } | null {
  if (bucket.is_other) {
    return {
      label: "folded cohort",
      hint:
        "The unlabelled remainder: every cohort too small to name, aggregated. It has no label to " +
        "attribute to anything, by design.",
    };
  }
  if (!bucket.recognised) {
    return {
      label: "not recognised",
      hint:
        "Either the explicit fold for a value Microsoft did not send, or a value NOVA has no " +
        "ordering for yet. Counted and shown either way — never dropped.",
    };
  }
  return null;
}

/**
 * The k-anonymity fold, stated rather than hidden.
 *
 * A board that silently drops rows lies by omission, so every floored breakdown
 * says what the floor removed — including when it removed nothing, because
 * "nothing was folded" and "some rows are missing" are the two readings a silent
 * caption leaves open (§5).
 */
export function suppressionCaption(
  suppression: DefenderCohortSuppression | null,
  noun: string,
): string | null {
  if (suppression == null) {
    return null;
  }
  if (suppression.cohorts_folded === 0) {
    return `No cohort fell below the k≥${suppression.min_events} floor — every row here is labelled.`;
  }
  return (
    `${formatCount(suppression.cohorts_folded)} cohorts with fewer than ${suppression.min_events} ` +
    `${noun} were folded into one unlabelled "Other" row carrying ` +
    `${formatCount(suppression.events_folded)} ${noun}. The total still adds up; those rows just ` +
    "have no label, so nothing here can single anything out."
  );
}

/** Whether a breakdown has anything to chart at all. */
export function hasBuckets(breakdown: DefenderBreakdown): boolean {
  return breakdown.buckets.length > 0 && bucketTotal(breakdown.buckets) > 0;
}

/* ------------------------------------------------------------------------ *
 * Formatters — every one renders `null` as an em dash
 * ------------------------------------------------------------------------ */

/** Thousands-separated integer, or an em dash. Never `0` for "unknown". */
export function formatCount(value: number | null | undefined): string {
  return formatCountShared(value);
}

/** A 0–100 value as a percentage with one decimal, or an em dash. */
export function formatPercent(value: number | null | undefined): string {
  return formatPercentShared(value);
}

/**
 * Secure Score points — a float from Graph, shown to one decimal, or an em dash.
 *
 * Not `formatCount`: control scores arrive fractional (`4.5` of `9`), and
 * rounding them to whole points would make two different gaps rank equal.
 */
export function formatPoints(value: number | null | undefined): string {
  if (value == null) {
    return EM_DASH;
  }
  return value.toLocaleString(undefined, { maximumFractionDigits: 1 });
}

/** A signed delta in points — `+2.4`, `−1.1` — or an em dash. */
export function formatPointsDelta(value: number | null | undefined): string {
  if (value == null) {
    return EM_DASH;
  }
  const sign = value > 0 ? "+" : value < 0 ? "−" : "±";
  return `${sign}${Math.abs(value).toLocaleString(undefined, { maximumFractionDigits: 1 })}`;
}

/** A whole number of days — `3 days`, `1 day` — or an em dash. */
export function formatDays(value: number | null | undefined): string {
  if (value == null) {
    return EM_DASH;
  }
  return `${formatCountShared(value)} ${value === 1 ? "day" : "days"}`;
}

/** A `YYYY-MM-DD` day as `20 Aug`, or an em dash. */
export function formatDay(value: string | null | undefined): string {
  if (value == null) {
    return EM_DASH;
  }
  const parsed = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleDateString(undefined, { day: "2-digit", month: "short", timeZone: "UTC" });
}

/**
 * Coerce a Recharts tooltip value to a number, or `null` when there is none.
 *
 * `Chart.TooltipContent` types its `formatter` as
 * `(value: number | string | undefined) => ReactNode` and calls it
 * unconditionally, so a series with no value at the hovered X arrives as
 * `undefined`. `Number(undefined)` is `NaN` and — worse — `Number(null)` is `0`,
 * which renders a fabricated zero. Both funnel into `null` here so the null-safe
 * formatters can do what they were written to do: render the gap as a gap (§5).
 * Never wrap a value in `Number()` on the way into a formatter; use this.
 */
export function tooltipValue(value: number | string | undefined): number | null {
  if (value == null || value === "") {
    return null;
  }
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

const WINDOW_PATTERN = /^P(\d+)D$/;

/**
 * An ISO 8601 window as a caption — `P14D` → `last 14 days`, `PT0S` → a
 * point-in-time read.
 *
 * Every panel states its own window because they genuinely differ (incidents
 * 14 days, alerts 7, Secure Score 90, identity health none), and none of them is
 * a filter anyone can change: the backend's windows are constants, deliberately,
 * so that no request input ever reaches a Graph `$filter`. That is why this board
 * has no `DateRangePicker` — a control that changed nothing would be a lie.
 */
export function formatWindow(window: string): string {
  if (window === "PT0S") {
    return "as it stands now — a point-in-time read, not a window";
  }
  const match = WINDOW_PATTERN.exec(window);
  if (!match) {
    return window;
  }
  const days = Number(match[1]);
  return `last ${days} ${days === 1 ? "day" : "days"}`;
}

/**
 * One Secure Score effort group, as a sentence.
 *
 * "12 points available in AzureAD controls at Low user impact" is what gets
 * scheduled; "aad_mfa is worth 10 points" is a fact someone still has to build a
 * plan out of. Both halves fold to plain words when Microsoft's catalogue did not
 * say, rather than printing `unknown · unknown` at a reader.
 */
export function effortLabel(service: string, userImpact: string): string {
  const product = service === "unknown" ? "controls with no product named" : `${service} controls`;
  const impact = userImpact === "unknown" ? "unstated user impact" : `${userImpact} user impact`;
  return `${product} at ${impact}`;
}

/** The same pairing, short enough for a chart's category axis. */
export function effortAxisLabel(service: string, userImpact: string): string {
  return `${service} · ${userImpact} impact`;
}

/** The `Ban` glyph, re-exported so the setup panel can mark what is never requested. */
export const NEVER_REQUESTED_ICON: IconComponent = Ban;
